import { useState, MouseEvent, useEffect, useMemo } from "react";


import PageHeader from "../../../components/Page/PageHeader";
import SnackBar from "../../../components/Alerts/SnackBar";
import useMessage from "../../../hooks/useMessage";

import ColumnsButton from "../../../components/Buttons/ColumnsButton";
import { TableData, TableDataField, TableHeader } from "../../../components/Table/DataTable";
import { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import SearchBar from "../../../components/Filtering/SearchBar";
import { useNavigate } from "react-router-dom";
import { useGetDataFilesCountDataFileCountGet, useGetDataFilesDataFileGet, useGetDataFileTagsDataFileTagsGet } from "../../../api/generated/fastAPI";
import { DataFile, GetDataFilesDataFileGetParams } from "../../../api/models";
import FilterBar, { FilterBarType } from "../../../components/Filtering/FilterBar";
import ErrorScreen from "../../../components/Alerts/ErrorScreen";
import useDataParams, { GetParamsTypes } from "../../../hooks/useDataParams";
import UploadFileModal from "../components/UploadFileModal";
import GenerateDatasetModal from "../../Datasets/components/GenerateDatasetModal";
import DeleteModal, { DeleteItemType } from "../../../components/Popups/DeleteModal";
import UpdatedPagination from "../../../components/Filtering/UpdatedPagination";
import { exportFileExtensions } from "../../../utils/supplementalData";
import { exportData, formatDateString } from "../../../utils/formattingFns";
import NestedDropdown from "../../../components/Menus&Dropdowns/NestedDropdown";
import useDelete from "../../../hooks/endpoints/useDelete";
import AccessGate from "../../../components/Auth/AccessGate";
import { useAccess } from "../../../access/useAccess";
import { demo_access } from "../../../access/predefinedConditions";
import VirtualizedDataTable from "../../../components/Table/VirtualizedDataTable";


const fileHeaders: TableHeader[] = [
    {
        label: 'Name',
        value: 'name',
        isShown: true,
        alignment: 'left',
    },
    {
        label: 'Records',
        value: 'records',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Tags',
        value: 'tags',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Created By',
        value: 'created_by',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Owned By',
        value: 'owner',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Date Created',
        value: 'creation_date',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Date Updated',
        value: 'updated_date',
        isShown: true,
        alignment: 'center',
    },
]

const defaultParams: GetDataFilesDataFileGetParams = {
    name: null,
    created_by: null,
    owner: null,
    start_date: null,
    end_date: null,
    tag_groups: null,
    sort_by: 'creation_date',
    sort_order: 'desc',
    offset: 0,
    limit: 10,
}


const defaultFilters: FilterBarType[] = [
    {
        label: 'Created Date Range',
        param: 'start_date',
        type: 'date-range',
        isDefault: true,
        groupKey: 'date-range',
    },
    {
        label: 'Created Date Range',
        param: 'end_date',
        type: 'date-range',
        isDefault: true,
        groupKey: 'date-range',
    },
    {
        label: 'Tags',
        param: 'tag_groups',
        type: 'tags',
        isDefault: true,
    },
    {
        label: 'Created By',
        param: 'created_by',
        type: 'users',
        isDefault: true,
    },
    {
        label: 'Owner',
        param: 'owner',
        type: 'users',
        isDefault: true,
    },
]




export default function Files() {
    const { canAccess } = useAccess();

    const {
        open,
        setOpen,
        message,
        isSuccess,
        setPopupMessage
    } = useMessage();

    const getFileTags = useGetDataFileTagsDataFileTagsGet();

    const {
        params,
        setParams,
        search,
        sort,
        filters,
        setPaginationLimit,
        setPaginationOffset
    } = useDataParams({
        defaultParams,
        defaultFilters,
        tagsData: getFileTags.data as unknown as { [key: string]: string[] },
    })


    const [ selectedItems, setSelectedItems ] = useState<(string | number)[]>([]);
    const [ tableHeaders, setTableHeaders ] = useState<TableHeader[]>(fileHeaders);
    const [ dataFiles, setDataFiles ] = useState<DataFile[]>([]);
    const [openGenerateDatasetId, setOpenGenerateDatasetId] = useState<string | null>(null);

    const navigate = useNavigate();
    const { data: fileData, isLoading: isDatafileLoading, refetch: refetchDatafiles, isError, error } = useGetDataFilesDataFileGet(params as GetParamsTypes);
    const getDatafileCountMutation = useGetDataFilesCountDataFileCountGet({
        name: (params as GetParamsTypes).name!,
        created_by: (params as GetParamsTypes).created_by!,
        owner: (params as GetParamsTypes).owner!,
        start_date: (params as GetParamsTypes).start_date!,
        end_date: (params as GetParamsTypes).end_date!,
        tag_groups: (params as GetParamsTypes).tag_groups!,
    });

    const refetchFiles = () => {
        refetchDatafiles();
        getDatafileCountMutation.refetch();
    }
    

    const { deleteFiles } = useDelete({ setPopupMessage, refetchFn: refetchFiles });

    const injectOptions = (datafile: DataFile) => {
        const id = datafile.file_hash!;
        const name = datafile.name!;

        let options: DropdownOption[] = [
            {
                label: 'Generate Dataset from File',
                action: () => setOpenGenerateDatasetId(id), // Only trigger open
            },
            {
                label: 'Export',
                action: (e:MouseEvent<HTMLButtonElement>) => {},
                moreOptions: exportFileExtensions.map(ext => (
                    {
                        label: ext.label,
                        action: async (e: MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            const filename = name.split('.')[0];
                            try {
                                await exportData(
                                    [{ name: filename, hash: id ? id : '' }],
                                    ext.file_type,
                                    'datafile',
                                );
    
                                setPopupMessage('You have successfully downloaded the selected file.', true);
                            } catch (err:any) {
                                console.log(err);
                                setPopupMessage('There was an error downloading this file. Please try again.', false);
                            }
                        }
                    }
                ))
            },
        ];

        if (canAccess({or: demo_access})) options.push({
            label: 'Delete',
            btnComp: (
                <DeleteModal 
                    items={[{
                        type: 'data_file',
                        id,
                        name,
                    }]}
                    deleteFn={deleteFiles}
                />
            ),
        });

        return options;
    }

    useEffect(() => {
        if (fileData) {
            setDataFiles(fileData as unknown as DataFile[]);
        }
    }, [fileData]);

    const tableData = useMemo(() => {
        if (dataFiles.length === 0) return [];

        const items: TableData[] = [];

        dataFiles.forEach(file => {
            const {
                file_hash,
                name,
                datafile_metadata,
                file_metadata,
                created_by,
                owner,
                creation_date,
                updated_date,
            } = file;

            const records = datafile_metadata && datafile_metadata.records_number ? datafile_metadata.records_number.toString() : '0';
            const tags = file_metadata && file_metadata.tags ? (file_metadata.tags).map(tag => tag ?? 'null') : [] as string[];

            const fileData: any = {
                name: name ?? '',
                records,
                tags,
                created_by: created_by ?? '',
                owner: owner ?? '',
                creation_date: creation_date ? formatDateString(creation_date) : '',
                updated_date: updated_date ? formatDateString(updated_date) : '',
            }

            const fields: TableDataField[] = fileHeaders.map(header => {
                const { alignment, value } = header;
                const field: TableDataField = {
                    associatedColumn: value,
                    value: fileData[value] ?? '',
                    alignment,
                    type: value === 'tags' ? 'tags' : 'text',
                    action: value === 'name' ? (e:MouseEvent<HTMLButtonElement>) => navigate(`/files/${file_hash}`) : undefined,
                }

                return field;
            });

            items.push({
                id: file_hash!,
                fields,
                options: injectOptions(file),
            })

        });

        return items;

        //eslint-disable-next-line
    }, [dataFiles]);


    const count = useMemo(() => {
        if (!getDatafileCountMutation.data) return 0;
        return getDatafileCountMutation.data as unknown as number;
    }, [getDatafileCountMutation.data]);

    const actionDropdownOptions = useMemo(() => {
        const options: DropdownOption[] = [];
        if (dataFiles.length > 0 && selectedItems.length > 0) {
            let items: DeleteItemType[] = [];
            let exportItems: { name: string, hash: string }[] = [];
            selectedItems.forEach(id => {
                const matchingItem = dataFiles.find(file => file.file_hash! === id);
                if (matchingItem) {
                    items.push({
                        type: 'data_file',
                        id,
                        name: matchingItem.name ? matchingItem.name : ''
                    })

                    exportItems.push({ hash: id as string, name: matchingItem.name ? matchingItem.name.split('.')[0] : '' });
                }
            });

            const deleteModalComp: DropdownOption = {
                label: 'Delete',
                btnComp: (
                    <DeleteModal 
                        items={items}
                        cancelFn={() => setSelectedItems([])}
                        deleteFn={deleteFiles}
                    />
                ),
            };

            const exportModalComp: DropdownOption = {
                label: 'Export',
                moreOptions: exportFileExtensions.map(ext => (
                    {
                        label: ext.label,
                        action: async (e: MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            try {
                                await exportData(
                                    exportItems,
                                    ext.file_type,
                                    'datafile',
                                );
    
                                setPopupMessage('You have successfully downloaded the selected files.', true);
                                setSelectedItems([]);
                            } catch (err:any) {
                                console.log(err);
                                setPopupMessage('There was an error downloading the selected files. Please try again.', false);
                            }
                        }
                    }
                ))
            };

            options.push(exportModalComp);
            if (canAccess({or: demo_access})) options.push(deleteModalComp);
        };

        return options;

        //eslint-disable-next-line
    }, [dataFiles, selectedItems])

    
    return (
        <div className="h-screen overflow-hidden flex flex-col justify-start items-stretch gap-4 p-10">
            <GenerateDatasetModal 
                selectedFileId={openGenerateDatasetId ?? undefined}
                isDropdownItem
                setPopupMessage={setPopupMessage}
                open={!!openGenerateDatasetId}
                setOpen={(val) => setOpenGenerateDatasetId(val ? openGenerateDatasetId : null)}
            />

            <PageHeader label='Files'>
                <AccessGate
                    or={demo_access}
                >
                    <UploadFileModal setPopupMessage={setPopupMessage} refetchFiles={refetchFiles}/>
                </AccessGate>
            </PageHeader>

            <div className="flex flex-col justify-start items-start gap-2">
                <div className="w-full flex justify-between items-center gap-4">
                    <div className="max-w-[600px] w-full">
                        <SearchBar
                            searchFn={search}
                            searchKey="name"
                        />
                    </div>
                    <div className="flex justify-end items-center gap-4">
                        <ColumnsButton 
                            headers={tableHeaders} 
                            setHeaders={setTableHeaders} 
                            isDisabled={false}
                        />
                        {selectedItems.length > 0 && (
                            <NestedDropdown 
                                dir="right"
                                btn={{
                                    label: `Actions (${selectedItems.length} selected)`,
                                    type: 'secondary',
                                    styling: 'solid',
                                    id: 'file-multi-action-btn',
                                }}
                                options={actionDropdownOptions}
                            />
                        )}
                    </div>
                </div>
                <FilterBar 
                    filters={filters}
                    setParams={setParams}
                    params={params}
                />
            </div>

            {isError && tableData.length === 0 ? (
                <ErrorScreen errCode={(error as any).code}/>
            ) : (
                <>
                <VirtualizedDataTable 
                    dataType="datafiles"
                    paramValues={Object.entries(params).filter(entry => ['name', 'start_date', 'end_date'].includes(entry[0]) && entry[1]).map(val => val[1])}
                    isLoading={isDatafileLoading} 
                    headers={tableHeaders} 
                    tableData={tableData}
                    selection={{
                        selectedItems,
                        setSelectedItems
                    }}
                    sorting={{
                        sortableHeaders: ['name', 'created_by', 'owner', 'creation_date', 'updated_date'],
                        sortHeaders: sort,
                        sortByParam: params.sort_by!,
                        sortOrderParam: params.sort_order!,
                    }}
                    setPopupMessage={setPopupMessage}
                    fixedItemHeight={72}
                    count={getDatafileCountMutation.data as unknown as number | undefined}
                />
                <UpdatedPagination 
                    count={count}
                    limit={params.limit!}
                    offset={params.offset!}
                    setPaginationLimit={setPaginationLimit}
                    setPaginationOffset={setPaginationOffset}
                    size="reg"
                    isLoading={getDatafileCountMutation.isLoading}
                />
                </>
            )}
     
            
            <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>  
        </div>
    )
}